import { FunctionComponent, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import UserInput from '@/components/_custom/UserInput/userInput'
import { useTranslation } from 'react-i18next'
import styles from './editBlacklist.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { CREATE_BLACKLIST, UPDATE_BLACKLIST } from '@/modules/API/software/blacklist'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface EditBlacklistProps {
    create: boolean
    open: boolean
    handleClose: () => void
    addBlacklistElement?: (value: string) => void
    updateBlacklistElement?: (oldEmail: string, newEmail: string) => void
    email?: string
}

const EditBlacklist: FunctionComponent<EditBlacklistProps> = ({
    create,
    open,
    handleClose,
    addBlacklistElement,
    updateBlacklistElement,
    email
}: EditBlacklistProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [editEmail, setEditEmail] = useState<string>(email || '')

    useEffect(() => {
        setEditEmail(email || '')
    }, [email])

    const [createOneBlacklistElement] = useMutation(CREATE_BLACKLIST, {
        variables: {
            email: editEmail
        },
        onCompleted: () => {
            if (addBlacklistElement) {
                addBlacklistElement(editEmail)
            }

            showNotificationSuccess(t('blacklistElementCreated'))
            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorCreatingBlacklistElement'))
        }
    })

    const [updateOneBlacklistElement] = useMutation(UPDATE_BLACKLIST, {
        variables: {
            email_search: email,
            email_replacement: editEmail
        },
        onCompleted: () => {
            if (updateBlacklistElement && email) {
                updateBlacklistElement(email, editEmail)
            }
            showNotificationSuccess(t('blacklistElementUpdated'))
            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorUpdatingBlacklistElement'))
        }
    })

    const disabled: boolean = create ? !editEmail : editEmail === email

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={create ? t('addBlacklistElement') : t('updateBlacklistElement')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <UserInput
                        label={t('domains')}
                        value={editEmail}
                        setValue={setEditEmail}
                        required
                        className={styles.Input}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={create ? 'add' : 'update'}
                        onClick={create ? createOneBlacklistElement : updateOneBlacklistElement}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default EditBlacklist
